import React, { FC, useEffect, useState } from 'react';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '../../../components/combobox/index';
import { useFormat } from '../../../hooks/index';
import { SnapshotListItem } from '../../../types/snapshot.types';

export type EventDateValue = {
  date: Date;
  id: string;
  isCompanyIncorporation?: boolean;
};

type CompanyIncorporationDateOption = {
  date: Date;
  id: string;
};

const EventDateItemsList: FC<{
  companyIncorporation?: CompanyIncorporationDateOption | null;
  snapshots: SnapshotListItem[];
}> = ({ snapshots, companyIncorporation }) => {
  const { format } = useFormat();
  return (
    <>
      {snapshots
        .filter(
          (
            snapshot,
          ): snapshot is { valuation: NonNullable<SnapshotListItem['valuation']> } & SnapshotListItem =>
            Boolean(snapshot.valuation),
        )
        .map(({ valuation }) => (
          <ComboboxOption
            key={valuation.id}
            value={JSON.stringify({ id: valuation.id, date: valuation.date })}
          >
            <span className="text-sm text-gray-700">{format(valuation.date, 'dd/MM/yyyy')}</span>
          </ComboboxOption>
        ))}
      {companyIncorporation && (
        <ComboboxOption
          key={companyIncorporation.id}
          value={JSON.stringify({
            id: companyIncorporation.id,
            date: companyIncorporation.date,
            isCompanyIncorporation: true,
          })}
        >
          <span className="text-sm text-gray-700">
            {format(companyIncorporation.date, 'dd/MM/yyyy')} (
            <AppFormattedMessage id="INCORPORATION" />)
          </span>
        </ComboboxOption>
      )}
    </>
  );
};

export type EventDateComboboxProps = {
  companyIncorporation?: CompanyIncorporationDateOption | null;
  defaultData?: EventDateValue;
  onSelect?: (state: EventDateValue) => void;
  snapshots: SnapshotListItem[];
};

export const EventDateCombobox: FC<EventDateComboboxProps> = ({
  defaultData,
  onSelect,
  snapshots,
  companyIncorporation,
}) => {
  const [value, setValue] = useState<EventDateValue | null>(defaultData || null);
  const { format } = useFormat();

  useEffect(() => {
    if (defaultData) {
      setValue(defaultData);
    }
  }, [defaultData]);

  return (
    <Combobox
      as={'div'}
      className="relative"
      onChange={(value: string) => {
        const parsedValue = JSON.parse(value) as EventDateValue;
        onSelect?.(parsedValue);
        setValue(parsedValue);
      }}
    >
      <div className="relative">
        <ComboboxInput
          className={'bg-gray-900'}
          icon={<ChevronDownIcon className="mt-2" />}
          onChange={() => ''}
          placeholder={<AppFormattedMessage id="EVENT_DATE" />}
          readOnly
          value={value ? format(value.date && value.date, 'dd/MM/yyyy') : ''}
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>
      <ComboboxOptions className="w-full">
        <EventDateItemsList companyIncorporation={companyIncorporation} snapshots={snapshots} />
      </ComboboxOptions>
    </Combobox>
  );
};
